import { useState, useRef, useEffect } from "react";
import axios from "axios";
import { Bot, X, Upload, Send, Download, Loader2, Sparkles, FileText, CheckCircle, AlertCircle } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

function AgentMessage({ msg }) {
  const isUser = msg.role === "user";
  return (
    <div className={`flex gap-2 ${isUser ? "flex-row-reverse" : "flex-row"}`}>
      {!isUser && (
        <div className="w-6 h-6 bg-[#A8A67A] rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
          <Bot className="w-3.5 h-3.5 text-white" strokeWidth={1.5} />
        </div>
      )}
      <div className={`max-w-[85%] ${isUser ? "items-end" : "items-start"} flex flex-col gap-1`}>
        <div className={`px-3 py-2 text-xs leading-relaxed ${
          isUser
            ? "bg-[#A8A67A] text-white rounded-tl-lg rounded-bl-lg rounded-br-lg"
            : "bg-white border border-border rounded-tr-lg rounded-br-lg rounded-bl-lg text-foreground"
        }`}>
          {msg.content}
        </div>
        {msg.retrieve_path && (
          <a
            href={`${API}/storage/download?path=${encodeURIComponent(msg.retrieve_path)}`}
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center gap-1.5 text-[10px] text-[#A8A67A] border border-[#D4D2B0] bg-[#F5F4EE] px-2.5 py-1.5 hover:bg-[#e8ede4] transition-colors"
            data-testid="agent-download-link"
          >
            <Download className="w-3 h-3" />
            {msg.retrieve_path.split("/").pop()}
          </a>
        )}
      </div>
    </div>
  );
}

function PlacementResult({ result, onNavigate }) {
  return (
    <div className="mx-3 mb-3 bg-[#F5F4EE] border border-[#D4D2B0] p-3 text-xs" data-testid="placement-result-card">
      <div className="flex items-center gap-1.5 mb-2">
        <CheckCircle className="w-3.5 h-3.5 text-emerald-600 flex-shrink-0" />
        <span className="font-semibold text-emerald-700">File placed successfully</span>
        <span className={`ml-auto text-[9px] font-semibold uppercase px-1.5 py-0.5 ${
          result.confidence === "high" ? "bg-emerald-100 text-emerald-700" :
          result.confidence === "medium" ? "bg-amber-100 text-amber-700" :
          "bg-gray-100 text-gray-600"
        }`}>{result.confidence}</span>
      </div>
      <p className="text-muted-foreground mb-1">
        <span className="font-medium text-foreground">{result.name}</span> → <button
          onClick={() => onNavigate(result.placed_in)}
          className="text-[#A8A67A] underline font-mono"
        >{result.placed_in}</button>
      </p>
      {result.reasoning && <p className="text-[10px] text-muted-foreground italic">{result.reasoning}</p>}
      {result.tags?.length > 0 && (
        <div className="flex flex-wrap gap-1 mt-2">
          {result.tags.map(t => (
            <span key={t} className="bg-white border border-border px-1.5 py-0.5 text-[9px] text-muted-foreground">{t}</span>
          ))}
        </div>
      )}
    </div>
  );
}

export default function DriveAgent({ onRefresh, onNavigate }) {
  const [messages, setMessages] = useState([
    {
      id: "welcome",
      role: "agent",
      content: "Hi! I'm your file assistant. Drop a file above for smart placement, or ask me anything — \"Where is the 2025 S&P data?\", \"Get me the Nitro report\", etc.",
    }
  ]);
  const [input, setInput] = useState("");
  const [thinking, setThinking] = useState(false);
  const [smartDragging, setSmartDragging] = useState(false);
  const [smartUploading, setSmartUploading] = useState(false);
  const [lastPlacement, setLastPlacement] = useState(null);
  const fileInputRef = useRef(null);
  const bottomRef = useRef(null);
  const inputRef = useRef(null);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages, lastPlacement]);

  const handleSmartDrop = async (files) => {
    if (!files.length) return;
    setSmartUploading(true);
    setLastPlacement(null);

    const file = files[0];
    const fd = new FormData();
    fd.append("file", file);

    try {
      const res = await axios.post(`${API}/storage/smart-upload`, fd);
      setLastPlacement(res.data);
      setMessages(prev => [...prev, {
        id: Date.now(),
        role: "agent",
        content: `I've placed "${res.data.name}" in ${res.data.placed_in}. ${res.data.reasoning || ""}`,
      }]);
      onRefresh(true); // true = also refresh sidebar root folders
    } catch (err) {
      setMessages(prev => [...prev, {
        id: Date.now(),
        role: "agent",
        content: `Sorry, I couldn't place that file: ${err.response?.data?.detail || err.message}`,
      }]);
    } finally {
      setSmartUploading(false);
    }

    for (let i = 1; i < files.length; i++) {
      const f = files[i];
      const fd2 = new FormData();
      fd2.append("file", f);
      try {
        const res = await axios.post(`${API}/storage/smart-upload`, fd2);
        setMessages(prev => [...prev, {
          id: Date.now() + i,
          role: "agent",
          content: `"${res.data.name}" → ${res.data.placed_in} (${res.data.confidence} confidence)`,
        }]);
        onRefresh(true);
      } catch {
        // silent
      }
    }
  };

  const sendMessage = async () => {
    const q = input.trim();
    if (!q || thinking) return;
    setInput("");
    setMessages(prev => [...prev, { id: Date.now(), role: "user", content: q }]);
    setThinking(true);
    try {
      const res = await axios.post(`${API}/storage/agent-chat`, { question: q });
      setMessages(prev => [...prev, {
        id: Date.now() + 1,
        role: "agent",
        content: res.data.answer,
        retrieve_path: res.data.retrieve_path || null,
      }]);
    } catch (err) {
      setMessages(prev => [...prev, {
        id: Date.now() + 1,
        role: "agent",
        content: `Error: ${err.response?.data?.detail || err.message}`,
      }]);
    } finally {
      setThinking(false);
    }
  };

  return (
    <aside className="w-80 flex flex-col bg-[#FAFAFA] border-l border-border flex-shrink-0" data-testid="drive-agent-panel">
      {/* Header */}
      <div className="px-4 py-3 border-b border-border bg-white flex items-center gap-2">
        <div className="w-6 h-6 bg-[#A8A67A] rounded-full flex items-center justify-center">
          <Bot className="w-3.5 h-3.5 text-white" strokeWidth={1.5} />
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-xs font-semibold text-foreground leading-none">File Agent</p>
          <p className="text-[9px] text-muted-foreground mt-0.5">AI-powered file assistant</p>
        </div>
        <div className="flex items-center gap-1">
          <div className="w-1.5 h-1.5 rounded-full bg-emerald-500" />
          <span className="text-[9px] text-muted-foreground">Online</span>
        </div>
      </div>

      {/* Smart Drop Zone */}
      <div
        className={`mx-3 mt-3 mb-0 border-2 border-dashed transition-all p-4 text-center cursor-pointer ${
          smartDragging
            ? "border-[#A8A67A] bg-[#F5F4EE]"
            : smartUploading
            ? "border-[#A8A67A]/40 bg-[#F5F4EE]"
            : "border-border hover:border-[#A8A67A]/50 hover:bg-[#F8F9FA]"
        }`}
        onDragOver={e => { e.preventDefault(); setSmartDragging(true); }}
        onDragLeave={e => { if (!e.currentTarget.contains(e.relatedTarget)) setSmartDragging(false); }}
        onDrop={e => { e.preventDefault(); setSmartDragging(false); handleSmartDrop([...e.dataTransfer.files]); }}
        onClick={() => !smartUploading && fileInputRef.current?.click()}
        data-testid="smart-drop-zone"
      >
        {smartUploading ? (
          <div className="flex flex-col items-center gap-1.5">
            <Loader2 className="w-5 h-5 text-[#A8A67A] animate-spin" />
            <p className="text-xs font-medium text-[#A8A67A]">AI is categorising…</p>
          </div>
        ) : (
          <div className="flex flex-col items-center gap-1.5">
            <div className="flex items-center gap-1">
              <Sparkles className="w-4 h-4 text-[#C4C29A]" strokeWidth={1.5} />
              <Upload className="w-4 h-4 text-[#A8A67A]" strokeWidth={1.5} />
            </div>
            <p className="text-xs font-semibold text-foreground">Smart Drop</p>
            <p className="text-[10px] text-muted-foreground leading-relaxed">Drop files here — AI will place them<br />in the right folder automatically</p>
          </div>
        )}
        <input
          ref={fileInputRef}
          type="file"
          multiple
          className="hidden"
          onChange={e => { handleSmartDrop([...e.target.files]); e.target.value = ""; }}
        />
      </div>

      {/* Last placement result */}
      {lastPlacement && (
        <div className="mt-2">
          <PlacementResult result={lastPlacement} onNavigate={(p) => { onNavigate(p); setLastPlacement(null); }} />
        </div>
      )}

      {/* Chat */}
      <div className="flex-1 overflow-y-auto px-3 py-3 space-y-3 min-h-0">
        {messages.map(msg => <AgentMessage key={msg.id} msg={msg} />)}
        {thinking && (
          <div className="flex gap-2">
            <div className="w-6 h-6 bg-[#A8A67A] rounded-full flex items-center justify-center flex-shrink-0">
              <Bot className="w-3.5 h-3.5 text-white" strokeWidth={1.5} />
            </div>
            <div className="bg-white border border-border px-3 py-2 rounded-tr-lg rounded-br-lg rounded-bl-lg">
              <div className="flex gap-1">
                {[0,1,2].map(i => (
                  <div key={i} className="w-1.5 h-1.5 bg-[#A8A67A] rounded-full animate-bounce" style={{ animationDelay: `${i * 0.15}s` }} />
                ))}
              </div>
            </div>
          </div>
        )}
        <div ref={bottomRef} />
      </div>

      {/* Input */}
      <div className="px-3 pb-3 pt-2 border-t border-border bg-white">
        <div className="flex gap-2">
          <input
            ref={inputRef}
            value={input}
            onChange={e => setInput(e.target.value)}
            onKeyDown={e => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); sendMessage(); } }}
            placeholder="Ask about your files…"
            className="flex-1 px-3 py-2 text-xs border border-border bg-[#FAFAFA] focus:outline-none focus:ring-1 focus:ring-[#A8A67A] rounded-none placeholder:text-muted-foreground"
            data-testid="agent-chat-input"
            disabled={thinking}
          />
          <button
            onClick={sendMessage}
            disabled={!input.trim() || thinking}
            className="px-3 py-2 bg-[#A8A67A] text-white hover:bg-[#8E8C64] transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex-shrink-0"
            data-testid="agent-send-btn"
          >
            <Send className="w-3.5 h-3.5" />
          </button>
        </div>
        <p className="text-[9px] text-muted-foreground mt-1.5 text-center">
          Ask: "Where is the S&P 2024 file?" · "Get me the Nitro report"
        </p>
      </div>
    </aside>
  );
}
